import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import React from 'react';
import ReactGA from 'react-ga4';
import { useI18n } from '../../i18n/I18nProvider';
import { Button } from '../ui/button';

interface HeroProps {
  leanpubUrl: string;
}

const Hero: React.FC<HeroProps> = ({ leanpubUrl }) => {
  const { t } = useI18n();

  const handleAmazonClick = () => {
    ReactGA.event({
      category: 'Clic',
      action: 'Botón Compra',
      label: 'Hero Section',
    });
  };

  return (
    <section className='pt-24 pb-12 md:pt-32 md:pb-20 overflow-hidden relative'>
      <motion.div
        className='absolute inset-0 bg-gradient-to-br from-white via-blue-50 to-purple-50'
        animate={{
          background: [
            'linear-gradient(to bottom right, #ffffff, #f0f9ff, #faf5ff)',
            'linear-gradient(to bottom right, #ffffff, #e0f2fe, #f5f3ff)',
            'linear-gradient(to bottom right, #ffffff, #dbeafe, #ede9fe)',
            'linear-gradient(to bottom right, #ffffff, #f0f9ff, #faf5ff)',
          ],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          repeatType: 'reverse',
        }}
      />
      <div className='container mx-auto px-4 sm:px-6 lg:px-8 relative z-10'>
        <div className='grid md:grid-cols-2 gap-8 items-center'>
          <motion.div
            className='order-2 md:order-1'
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <motion.h1
              className='text-3xl md:text-4xl lg:text-5xl font-bold font-heading mb-4 text-gray-900 leading-tight'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {t.hero.title}
            </motion.h1>
            <motion.h2
              className='text-xl md:text-2xl font-semibold mb-6 text-red-600'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {t.hero.subtitle}
            </motion.h2>
            <motion.p
              className='text-gray-700 text-lg mb-4 max-w-2xl leading-relaxed'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              {t.hero.description}
            </motion.p>
            <p className='text-gray-600 text-lg mb-4 max-w-2xl leading-relaxed italic'>
              {t.hero.story}
            </p>
            <motion.p
              className='text-gray-700 text-lg mb-8 max-w-2xl leading-relaxed'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              {t.hero.promise}
            </motion.p>
            <motion.div
              className='flex flex-col sm:flex-row gap-4'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
            >
              <Button asChild className='btn-primary'>
                <a href={leanpubUrl} target='_blank' rel='noopener noreferrer' onClick={handleAmazonClick}>
                  {t.all.ctaButton}
                </a>
              </Button>

              <Button variant='outline' asChild>
                <a href='#chapters' className='group'>
                  {t.hero.secondaryButton}
                  <ArrowRight className='ml-2 h-4 w-4 transition-transform group-hover:translate-x-1' />
                </a>
              </Button>
            </motion.div>
          </motion.div>
          <motion.div
            className='order-1 md:order-2 relative hidden md:block'
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <motion.div
              className='absolute -top-20 -right-20 w-64 h-64 bg-testingLib-blue opacity-10 rounded-full blur-3xl'
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.15, 0.1],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                repeatType: 'reverse',
              }}
            />
            <motion.div
              className='absolute -bottom-8 -left-8 w-40 h-40 bg-jest-red opacity-10 rounded-full blur-2xl'
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.1, 0.15, 0.1],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                repeatType: 'reverse',
              }}
            />

            <motion.div
              className='relative bg-white rounded-2xl shadow-xl p-6 md:p-8 z-10'
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <div className='bg-gray-100 rounded-lg p-4 mb-4 font-mono text-xs md:text-sm'>
                <pre className='max-w-full overflow-x-auto rounded-xl bg-slate-50 p-3 md:p-6 text-xs md:text-base'>
                  <code>{`import { render, screen } from '@testing-library/angular';
import { TestComponent } from './test.component';

describe('TestComponent', () => {
  it('render the title', async () => {
    await render(TestComponent);
    expect(screen.getByRole('heading')).toHaveTextContent('Hello Testing');
  });
});`}</code>
                </pre>
              </div>
              <div className='flex items-center justify-between'>
                <div className='flex space-x-2'>
                  <div className='w-3 h-3 bg-red-500 rounded-full'></div>
                  <div className='w-3 h-3 bg-yellow-500 rounded-full'></div>
                  <div className='w-3 h-3 bg-green-500 rounded-full'></div>
                </div>
                <div className='text-xs text-gray-600'>test.component.spec.ts</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
